import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '../contexts/CurrentUserContext';

export default function TrialBanner({ setFilter, onUpgrade }) {
  const { currentUser } = useCurrentUser();
  const { t } = useTranslation('common');

  const info = useMemo(() => {
    if (!currentUser) return null;

    // Covered members (household or a non-owner team member) are billed via
    // someone else's subscription — never show them the trial nudge.
    if (currentUser.isCoveredMember) return null;

    const status = currentUser.subscriptionStatus;
    // Hide for active paid subscriptions
    if (status === 'active') return null;

    const trialEnd = currentUser.trialEndsAt
      ? new Date(currentUser.trialEndsAt).getTime()
      : new Date(currentUser.createdAt).getTime() + 14 * 24 * 60 * 60 * 1000;

    const daysLeft = Math.max(0, Math.ceil((trialEnd - Date.now()) / (1000 * 60 * 60 * 24)));
    const daysPassed = 14 - daysLeft;

    // Show nothing in the first 10 days
    if (daysPassed < 10 && status !== 'trialing') return null;
    if (status === 'trialing' && daysLeft > 4) return null;

    return { daysLeft };
  }, [currentUser]);

  if (!info) return null;

  const { daysLeft } = info;
  const isUrgent = daysLeft <= 1;
  const isWarning = daysLeft <= 3;

  const bg = isUrgent ? '#dc2626' : isWarning ? '#d97706' : '#0C2D48';
  const text = isUrgent
    ? t('trial.ends_today')
    : daysLeft === 1
      ? t('trial.ends_in_one')
      : t('trial.ends_in_other', { count: daysLeft });

  return (
    <div
      style={{
        background: bg,
        color: '#fff',
        padding: '0.55rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.25rem',
        fontSize: '0.85rem',
        fontFamily: "'Raleway', sans-serif",
        fontWeight: 600,
        zIndex: 9999,
        flexShrink: 0,
      }}
    >
      <span>{text}</span>
      <button
        onClick={() => {
          if (onUpgrade) {
            onUpgrade();
            return;
          }
          if (setFilter) setFilter('Settings');
        }}
        aria-label={t('trial.choose_plan')}
        style={{
          background: 'rgba(255,255,255,0.2)',
          border: '1.5px solid rgba(255,255,255,0.5)',
          color: '#fff',
          padding: '0.3rem 1rem',
          borderRadius: '20px',
          fontFamily: "'Raleway', sans-serif",
          fontWeight: 700,
          fontSize: '0.8rem',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 0.15s',
        }}
        onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
        onMouseOut={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
      >
        {t('trial.choose_plan')}
      </button>
    </div>
  );
}
